import type {
  BidderReportData,
  ReportSection,
} from 'components/molecules/bidding_summary_bidder_report_pdf/report_data';
import { PossibleCompetitorsItem } from 'hooks/usePossibleCompetitors';
import { PossibleCompetitorCompany } from 'hooks/usePossibleCompetitorCompanies';
import { formatIsoDateTimeBr } from 'services/format_iso_date_br';
import { money, plural } from 'services/ai_management_format';
import { companyBadges } from 'services/ai_management_possible_competitors';

const COMPANY_COLUMNS = [
  { label: 'Empresa', width: 34 },
  { label: 'CNPJ', width: 18 },
  { label: 'Média de preço', width: 16, align: 'right' as const },
  { label: 'Situação', width: 32 },
];

const companyRow = (company: PossibleCompetitorCompany) => [
  company.name ?? '—',
  company.cnpj,
  money(company.averagePrice),
  companyBadges(company)
    .map((badge) => badge.text)
    .join(' · '),
];

const itemSection = (
  item: PossibleCompetitorsItem,
  companies: PossibleCompetitorCompany[],
): ReportSection => ({
  title: item.label,
  blocks: [
    {
      kind: 'rows',
      rows: [
        ...(item.catalog ? [{ label: 'Item de catálogo', value: item.catalog }] : []),
        ...(item.nationwide
          ? [{ label: 'Abrangência', value: 'Brasil (sem resultado no estado)' }]
          : []),
        { label: 'Empresas', value: item.companies.toLocaleString('pt-BR') },
        { label: 'Licitações parecidas', value: item.biddings.toLocaleString('pt-BR') },
        { label: 'Média de preço contratado', value: money(item.averagePrice) },
      ],
    },
    companies.length
      ? {
          kind: 'table',
          columns: COMPANY_COLUMNS,
          rows: companies.map(companyRow),
        }
      : { kind: 'text', text: 'Nenhuma empresa venceu este item no período.' },
  ],
});

export const buildPossibleCompetitorsReport = ({
  items,
  companiesByKey,
  biddingNumber,
  publicBody,
  conlicitacaoId,
  state,
  period,
}: {
  items: PossibleCompetitorsItem[];
  companiesByKey: Record<string, PossibleCompetitorCompany[]>;
  biddingNumber: string;
  publicBody: string;
  conlicitacaoId: string;
  state: string | null;
  period: number;
}): BidderReportData => ({
  biddingNumber,
  publicBody,
  conlicitacaoId,
  generatedAt: formatIsoDateTimeBr(new Date().toISOString()),
  sections: [
    {
      title: 'Possíveis Concorrentes',
      blocks: [
        {
          kind: 'text',
          text: `Empresas que venceram os itens selecionados em licitações com objeto parecido${
            state ? ` no estado ${state}` : ''
          } nos últimos ${plural(period, 'ano', 'anos')}.`,
        },
      ],
    },
    ...items.map((item) => itemSection(item, companiesByKey[item.key] ?? [])),
  ],
});
